import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth, homeFor } from "../context/AuthContext";
import { ShieldCheck, Clock, Zap, Eye, Lock, CheckCircle2, Users, FileCheck } from "lucide-react";
import { toast } from "sonner";

const FEATURES = [
  { icon: Zap, title: "60-second documentation", desc: "Complete scheduled support blocks in under one minute." },
  { icon: Eye, title: "Live coordinator visibility", desc: "Monitor student support in real time across every assigned aide." },
  { icon: Lock, title: "FERPA-aligned security", desc: "Role-based access, audit trails, and school-level data isolation." },
];

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otpStep, setOtpStep] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const idleLocked = searchParams.get("idle") === "1";
  const [workspace, setWorkspace] = useState(null);
  const [pwStep, setPwStep] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotInfo, setForgotInfo] = useState(null);
  const { completeLogin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { api.get("/public/workspace").then((r) => setWorkspace(r.data)).catch(() => {}); }, []);

  const finishLogin = (data) => {
    if (data.user.must_change_password) {
      localStorage.setItem("sf_token", data.access_token);
      setPwStep(data);
    } else {
      completeLogin(data.access_token, data.user);
      navigate(homeFor(data.user.role));
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setForgotInfo(data);
    } catch (err) { setError(apiError(err)); }
    setLoading(false);
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPw !== confirmPw) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { new_password: newPw });
      toast.success("Password set — welcome to SupportPro Hub");
      completeLogin(pwStep.access_token, { ...pwStep.user, must_change_password: false });
      navigate(homeFor(pwStep.user.role));
    } catch (err) { setError(apiError(err)); }
    setLoading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: email.trim(), password: password.trim() });
      if (data.mfa_required) {
        setOtpStep(data);
      } else {
        finishLogin(data);
      }
    } catch (err) { setError(apiError(err)); }
    setLoading(false);
  };

  const verify = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { otp_token: otpStep.otp_token, code: code.trim() });
      finishLogin(data);
    } catch (err) { setError(apiError(err)); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#F7F9F8]">
      <div className="lg:w-[58%] flex flex-col px-6 sm:px-12 pt-8 lg:pt-10 pb-10 lg:min-h-screen">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-xl bg-[#087F5B] flex items-center justify-center font-heading font-bold text-white text-sm shadow-sm">SP</span>
          <div>
            <p className="font-heading text-lg font-bold text-[#132238] tracking-tight leading-none">SupportPro Hub</p>
            <p className="text-[11px] text-[#637083] mt-0.5">by The Allicock Group</p>
          </div>
        </div>

        <div className="mt-10 lg:mt-14 max-w-xl fade-up">
          <h1 className="font-heading font-semibold tracking-tight text-[#132238] text-4xl sm:text-5xl leading-[1.08]">
            Every block documented.<br />Every student visible.
          </h1>
          <p className="text-[#637083] text-base mt-5 leading-relaxed max-w-lg">
            Real-time student support documentation, aide accountability, implementation monitoring, and coordinator visibility in one secure workspace.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl stagger">
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="sf-card-hover bg-white border border-[#E5E8EC] rounded-2xl p-4 shadow-sm">
              <span className="inline-flex p-2 rounded-lg bg-[#087F5B]/10 text-[#087F5B] mb-2.5"><Icon size={16} /></span>
              <p className="text-sm font-semibold text-[#132238]">{title}</p>
              <p className="text-xs text-[#637083] mt-1 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 relative max-w-3xl hidden sm:block" aria-hidden="true">
          <div className="sf-float bg-white border border-[#E5E8EC] rounded-2xl shadow-[0_24px_60px_-20px_rgba(19,34,56,0.25)] overflow-hidden">
            <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-[#E5E8EC] bg-[#F7F9F8]">
              <span className="w-2.5 h-2.5 rounded-full bg-[#E5E8EC]" /><span className="w-2.5 h-2.5 rounded-full bg-[#E5E8EC]" /><span className="w-2.5 h-2.5 rounded-full bg-[#E5E8EC]" />
              <p className="ml-3 text-[11px] text-[#637083]">SupportPro Hub · School Overview</p>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-4 gap-3">
                {[["Students Active", "12", Users], ["Documentation", "94%", FileCheck], ["Aides Checked In", "8", CheckCircle2], ["Follow-ups", "2", Clock]].map(([label, value, Icon]) => (
                  <div key={label} className="border border-[#E5E8EC] rounded-xl p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-[9px] font-semibold uppercase tracking-wide text-[#637083]">{label}</p>
                      <Icon size={12} className="text-[#087F5B]" />
                    </div>
                    <p className="font-heading text-xl font-bold text-[#132238] mt-1">{value}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="border border-[#E5E8EC] rounded-xl p-3.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-[#637083] mb-2.5">Support Completion %</p>
                  <div className="flex items-end gap-1.5 h-16">
                    {[55, 70, 62, 85, 78, 92, 88, 94, 90, 96].map((h, i) => (
                      <span key={i} className="sf-bar flex-1 rounded-t-sm bg-[#087F5B]/80" style={{ height: `${h}%`, animationDelay: `${i * 40}ms` }} />
                    ))}
                  </div>
                </div>
                <div className="border border-[#E5E8EC] rounded-xl p-3.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-[#637083] mb-2.5">Today's Schedule</p>
                  <div className="space-y-2">
                    {[["Math · 10:00–10:45", true], ["Reading Groups · 12:10", true], ["Centers · 13:10", false]].map(([label, done]) => (
                      <div key={label} className="flex items-center gap-2">
                        <CheckCircle2 size={13} className={done ? "text-[#087F5B]" : "text-[#E5E8EC]"} />
                        <p className="text-[11px] text-[#132238] flex-1">{label}</p>
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${done ? "bg-[#087F5B]/10 text-[#087F5B]" : "bg-[#F7F9F8] text-[#637083]"}`}>{done ? "Done" : "Due"}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3">
                    <div className="flex justify-between text-[9px] text-[#637083] mb-1"><span>Independence Score</span><span className="font-bold text-[#087F5B]">82</span></div>
                    <div className="h-1.5 bg-[#F7F9F8] rounded-full overflow-hidden"><span className="block h-full w-[82%] bg-[#087F5B] rounded-full" /></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-auto pt-8 hidden lg:flex items-center gap-4 text-[11px] text-[#637083]">
          <span>© 2026 The Allicock Group</span>
          <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-[#132238] transition-colors duration-200">Privacy</Link>
          <Link to="/terms" data-testid="footer-terms-link" className="hover:text-[#132238] transition-colors duration-200">Terms</Link>
          <Link to="/support" data-testid="footer-support-link" className="hover:text-[#132238] transition-colors duration-200">Support</Link>
        </div>
      </div>
      <div className="lg:w-[42%] flex items-center justify-center px-4 sm:px-8 py-10 lg:min-h-screen">
        <div className="w-full max-w-[450px] fade-up">
          {workspace?.name && (
            <div data-testid="workspace-banner" className="flex items-center gap-3 mb-5 bg-white border border-[#E5E8EC] rounded-2xl px-4 py-3 shadow-sm">
              {workspace.logo && <img src={workspace.logo} alt={workspace.name} className="h-8 rounded-md bg-[#132238] p-0.5 object-contain" />}
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#637083]">Welcome to</p>
                <p className="text-sm font-semibold text-[#132238]">{workspace.name} SupportPro Hub Workspace</p>
              </div>
            </div>
          )}
          <div className="bg-white rounded-[18px] p-7 sm:p-9 shadow-[0_20px_50px_-20px_rgba(19,34,56,0.18)] border border-[#E5E8EC]">
          {idleLocked && !otpStep && (
            <p data-testid="idle-lock-notice" className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-3 mb-4">
              <Clock size={14} className="shrink-0 mt-0.5" /> You were signed out after 30 minutes of inactivity (shared-device security policy). Please sign in again.
            </p>
          )}
          {pwStep ? (
            <form onSubmit={submitNewPassword} className="space-y-5">
              <div>
                <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#132238]">Create your password</h1>
                <p className="text-sm text-[#637083] mt-1">Hi {pwStep.user.name.split(" ")[0]} — set a personal password to replace your temporary one.</p>
              </div>
              <div>
                <label htmlFor="new-password" className="text-sm font-medium text-[#132238]">New password</label>
                <input id="new-password" data-testid="new-password-input" type="password" required minLength={8} value={newPw} onChange={(e) => setNewPw(e.target.value)}
                  className="mt-1.5 w-full rounded-xl border border-[#E5E8EC] px-4 py-3 text-base text-[#132238] focus:outline-none focus:ring-2 focus:ring-[#087F5B]/25 focus:border-[#087F5B] transition-colors duration-200" placeholder="At least 8 characters, letters + numbers" />
              </div>
              <div>
                <label htmlFor="confirm-password" className="text-sm font-medium text-[#132238]">Confirm password</label>
                <input id="confirm-password" data-testid="confirm-password-input" type="password" required minLength={8} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                  className="mt-1.5 w-full rounded-xl border border-[#E5E8EC] px-4 py-3 text-base text-[#132238] focus:outline-none focus:ring-2 focus:ring-[#087F5B]/25 focus:border-[#087F5B] transition-colors duration-200" placeholder="Re-enter your new password" />
              </div>
              {error && <p data-testid="set-password-error" className="text-sm text-red-600">{error}</p>}
              <button data-testid="set-password-btn" disabled={loading}
                className="w-full bg-[#087F5B] hover:bg-[#076B4D] text-white font-semibold rounded-xl py-3.5 text-base transition-colors duration-200 active:scale-[0.99] disabled:opacity-60 shadow-sm">
                {loading ? "Saving…" : "Set password & continue"}
              </button>
            </form>
          ) : forgotMode ? (
            <form onSubmit={submitForgot} className="space-y-5">
              <div>
                <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#132238]">Reset your password</h1>
                <p className="text-sm text-[#637083] mt-1">Enter your work email and we'll send you a secure reset link.</p>
              </div>
              {forgotInfo ? (
                <div className="space-y-4">
                  <p data-testid="forgot-success-message" className="text-sm bg-[#087F5B]/10 border border-[#087F5B]/20 text-[#087F5B] rounded-xl p-3.5">{forgotInfo.message}</p>
                  {forgotInfo.demo_reset_link && (
                    <p data-testid="demo-reset-link" className="text-xs bg-sky-50 border border-sky-200 text-sky-800 rounded-xl p-3 break-all">
                      Pilot mode — email delivery not yet connected. Your reset link:{" "}
                      <a href={forgotInfo.demo_reset_link} className="font-semibold underline">{forgotInfo.demo_reset_link}</a>
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <div>
                    <label htmlFor="forgot-email" className="text-sm font-medium text-[#132238]">Email</label>
                    <input id="forgot-email" data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                      className="mt-1.5 w-full rounded-xl border border-[#E5E8EC] px-4 py-3 text-base text-[#132238] placeholder:text-[#637083]/60 focus:outline-none focus:ring-2 focus:ring-[#087F5B]/25 focus:border-[#087F5B] transition-colors duration-200" placeholder="you@school.edu" />
                  </div>
                  {error && <p data-testid="forgot-error" className="text-sm text-red-600">{error}</p>}
                  <button data-testid="forgot-submit-btn" disabled={loading}
                    className="w-full bg-[#087F5B] hover:bg-[#076B4D] text-white font-semibold rounded-xl py-3.5 text-base transition-colors duration-200 active:scale-[0.99] disabled:opacity-60 shadow-sm">
                    {loading ? "Sending…" : "Send reset link"}
                  </button>
                </>
              )}
              <button type="button" data-testid="forgot-back-btn" onClick={() => { setForgotMode(false); setForgotInfo(null); setError(""); }}
                className="w-full text-xs text-[#637083] hover:text-[#132238] transition-colors duration-200">Back to sign in</button>
            </form>
          ) : !otpStep ? (
            <form onSubmit={submit} className="space-y-5">
              <div>
                <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#132238]">Welcome back</h1>
                <p className="text-sm text-[#637083] mt-1">Sign in to your SupportPro Hub workspace.</p>
              </div>
              <div>
                <label htmlFor="login-email" className="text-sm font-medium text-[#132238]">Email</label>
                <input id="login-email" data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 w-full rounded-xl border border-[#E5E8EC] px-4 py-3 text-base text-[#132238] placeholder:text-[#637083]/60 focus:outline-none focus:ring-2 focus:ring-[#087F5B]/25 focus:border-[#087F5B] transition-colors duration-200" placeholder="you@school.edu" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label htmlFor="login-password" className="text-sm font-medium text-[#132238]">Password</label>
                  <button type="button" data-testid="forgot-password-link" onClick={() => { setForgotMode(true); setError(""); }}
                    className="text-xs font-semibold text-[#087F5B] hover:text-[#076B4D] transition-colors duration-200">Forgot password?</button>
                </div>
                <input id="login-password" data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5 w-full rounded-xl border border-[#E5E8EC] px-4 py-3 text-base text-[#132238] placeholder:text-[#637083]/60 focus:outline-none focus:ring-2 focus:ring-[#087F5B]/25 focus:border-[#087F5B] transition-colors duration-200" placeholder="••••••••" />
              </div>
              {error && <p data-testid="login-error" className="text-sm text-red-600">{error}</p>}
              <button data-testid="login-submit-btn" disabled={loading}
                className="w-full bg-[#087F5B] hover:bg-[#076B4D] text-white font-semibold rounded-xl py-3.5 text-base transition-colors duration-200 active:scale-[0.99] disabled:opacity-60 shadow-sm">
                {loading ? "Signing in…" : "Sign in"}
              </button>
              <div className="flex items-center gap-3">
                <span className="flex-1 h-px bg-[#E5E8EC]" />
                <span className="text-[11px] text-[#637083]">or</span>
                <span className="flex-1 h-px bg-[#E5E8EC]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button type="button" data-testid="sso-microsoft-btn" onClick={() => toast.info("Microsoft SSO is coming soon for districts")}
                  className="flex items-center justify-center gap-2 border border-[#E5E8EC] rounded-xl py-2.5 text-sm font-medium text-[#132238] hover:bg-[#F7F9F8] transition-colors duration-200">
                  <svg width="14" height="14" viewBox="0 0 21 21" aria-hidden="true"><rect x="1" y="1" width="9" height="9" fill="#F25022"/><rect x="11" y="1" width="9" height="9" fill="#7FBA00"/><rect x="1" y="11" width="9" height="9" fill="#00A4EF"/><rect x="11" y="11" width="9" height="9" fill="#FFB900"/></svg>
                  Microsoft
                </button>
                <button type="button" data-testid="sso-google-btn" onClick={() => toast.info("Google SSO is coming soon for districts")}
                  className="flex items-center justify-center gap-2 border border-[#E5E8EC] rounded-xl py-2.5 text-sm font-medium text-[#132238] hover:bg-[#F7F9F8] transition-colors duration-200">
                  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                  Google
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-5">
              <div className="flex items-center gap-2 text-[#087F5B]"><ShieldCheck size={20} /><h1 className="font-heading text-2xl font-semibold tracking-tight text-[#132238]">Verification required</h1></div>
              <p className="text-sm text-[#637083]">{otpStep.message}</p>
              {otpStep.demo_otp && (
                <p data-testid="demo-otp-notice" className="text-xs bg-sky-50 border border-sky-200 text-sky-800 rounded-xl p-3">
                  Pilot mode — email delivery not yet connected. Your code: <span className="font-mono font-bold">{otpStep.demo_otp}</span>
                </p>
              )}
              <input data-testid="otp-code-input" inputMode="numeric" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value)} aria-label="Verification code"
                className="w-full rounded-xl border border-[#E5E8EC] px-3 py-3.5 text-center text-2xl tracking-[0.5em] font-mono text-[#132238] focus:outline-none focus:ring-2 focus:ring-[#087F5B]/25 focus:border-[#087F5B]" placeholder="000000" />
              {error && <p data-testid="otp-error" className="text-sm text-red-600">{error}</p>}
              <button data-testid="otp-submit-btn" disabled={loading}
                className="w-full bg-[#087F5B] hover:bg-[#076B4D] text-white font-semibold rounded-xl py-3.5 text-base transition-colors duration-200 active:scale-[0.99] disabled:opacity-60 shadow-sm">
                {loading ? "Verifying…" : "Verify & continue"}
              </button>
              <button type="button" data-testid="otp-back-btn" onClick={() => { setOtpStep(null); setCode(""); setError(""); }} className="w-full text-xs text-[#637083] hover:text-[#132238] transition-colors duration-200">Back to sign in</button>
            </form>
          )}
          </div>
          <div className="mt-6 grid grid-cols-3 gap-2">
            {[[ShieldCheck, "Protected Workspace"], [Users, "Role-Based Access"], [FileCheck, "Activity Logged"]].map(([Icon, label]) => (
              <div key={label} className="flex flex-col items-center gap-1.5 text-center">
                <Icon size={15} className="text-[#087F5B]" />
                <p className="text-[11px] text-[#637083] font-medium">{label}</p>
              </div>
            ))}
          </div>
          <div className="lg:hidden mt-8 flex items-center justify-center gap-4 text-[11px] text-[#637083]">
            <span>© 2026 The Allicock Group</span>
            <Link to="/privacy" className="hover:text-[#132238]">Privacy</Link>
            <Link to="/terms" className="hover:text-[#132238]">Terms</Link>
            <Link to="/support" className="hover:text-[#132238]">Support</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
